import { TEST_DURATION_MINUTES } from '../config/testMeta';

export const translations = {
  // ─────────────────────────────────────────────────────────────────────────
  // ENGLISH
  // ─────────────────────────────────────────────────────────────────────────
  en: {
    common: {
      loading: 'Loading…',
      retry: 'Retry',
      save: 'Save',
      cancel: 'Cancel',
      submit: 'Submit',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      close: 'Close',
      confirm: 'Confirm',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View →',
      copy: 'Copy',
      copied: 'Copied!',
      search: 'Search',
      filter: 'Filter',
      sortBy: 'Sort by',
      status: 'Status',
      type: 'Type',
      all: 'All',
      none: 'None',
      yes: 'Yes',
      no: 'No',
      or: 'or',
      and: 'and',
      by: 'by',
      of: 'of',
      page: 'Page',
      error: 'Error',
      success: 'Success',
      required: 'Required',
      optional: 'Optional',
      home: 'Home',
      dashboard: 'Dashboard',
      profile: 'Profile',
      logout: 'Logout',
      login: 'Log In',
      register: 'Register',
      notFound: 'Not found',
      somethingWrong: 'Something went wrong. Please try again.',
      noResults: 'No results found.',
      allTypes: 'All Types',
      word: 'word',
      words: 'words',
      challenge: 'challenge',
      challenges: 'challenges',
      theory: 'theory',
      theories: 'theories',
      sortLatest: 'Latest',
      sortPopular: 'Most Popular',
      sortTop: 'Top Rated',
      sortOldest: 'Oldest',
    },
    status: {
      active: 'Active',
      upcoming: 'Upcoming',
      closed: 'Closed',
      archived: 'Archived',
      published: 'Published',
      pending: 'Pending',
      rejected: 'Rejected',
      submitted: 'Submitted',
      under_review: 'Under Review',
      draft: 'Draft',
      winner: 'Winner',
      top20: 'Top 20',
      top100: 'Top 100',
      reviewing: 'Reviewing',
    },
    nav: {
      newWordPrize: 'New Word Prize',
      test: 'Test',
      thoughtChallenge: 'Thought Challenge',
      learningTheory: 'Learning Theory',
      profile: 'Profile',
      myInvites: 'My Invites',
      reputation: 'Reputation',
      logout: 'Logout',
      login: 'Login',
      register: 'Register',
      openMenu: 'Open navigation menu',
      closeMenu: 'Close navigation menu',
    },
    home: {
      heroEyebrow: 'Future Capability Platform',
      heroTitle: 'Map and grow your capability for the future',
      heroSub: 'A community platform for structured learning, collaborative challenges, vocabulary building, and a starting-point capability assessment.',
      heroCta: 'Get Started',
      heroSecondary: 'Learn More',
      featuresTitle: 'Four ways to build capability',
      featuresSub: 'Each module develops a different dimension of future-ready thinking.',
      testTitle: 'Future Capability Test',
      testDesc: 'A structured assessment across the core dimensions of capability.',
      testCta: 'Take the Test',
      wordTitle: 'New Word Prize',
      wordDesc: 'Coin new vocabulary to describe emerging concepts in technology, organization, and lifestyle.',
      wordCta: 'Explore Words',
      challengeTitle: 'Thought Challenges',
      challengeDesc: 'Engage with open-ended questions that have no single answer — and earn recognition for exceptional thinking.',
      challengeCta: 'View Challenges',
      theoryTitle: 'Learning Theory Prize',
      theoryDesc: 'Propose and share theories about how humans learn, adapt, and grow in the age of AI.',
      theoryCta: 'Browse Theories',
      howTitle: 'How it works',
      howStep1: 'Create an account',
      howStep1Desc: 'Join with an invite code from an existing member.',
      howStep2: 'Complete the test',
      howStep2Desc: 'Take the Future Capability Test to establish your baseline score.',
      howStep3: 'Contribute and earn',
      howStep3Desc: 'Submit words, theories, and challenge answers to earn Future Capability Points.',
      howStep4: 'Rise through tiers',
      howStep4Desc: 'Progress through tiers as you contribute and take part.',
      ctaTitle: 'Ready to map your future capability?',
      ctaSub: 'Join a community of thinkers building the skills the future demands.',
      ctaBtn: 'Create Your Account',
      leaderboardLink: 'View Leaderboard',
    },
    auth: {
      loginTitle: 'Welcome back',
      loginSub: 'Sign in to your account',
      email: 'Email address',
      emailPlaceholder: 'you@example.com',
      password: 'Password',
      passwordPlaceholder: 'Your password',
      showPassword: 'Show',
      hidePassword: 'Hide',
      forgotPassword: 'Forgot password?',
      loginBtn: 'Sign In',
      loggingIn: 'Signing in…',
      noAccount: "Don't have an account?",
      registerLink: 'Create one',
      registerTitle: 'Create your account',
      registerSub: 'Join with an invite code',
      name: 'Full name',
      namePlaceholder: 'Your full name',
      inviteCode: 'Invite code',
      inviteCodePlaceholder: 'Enter your invite code',
      inviteCodeHint: 'Optional — enter a code only if you were given one.',
      confirmPassword: 'Confirm password',
      confirmPasswordPlaceholder: 'Repeat your password',
      passwordHint: 'At least 8 characters including an uppercase letter, a lowercase letter, and a number',
      registerBtn: 'Create Account',
      registering: 'Creating account…',
      haveAccount: 'Already have an account?',
      loginLink: 'Sign in',
      errors: {
        emailRequired: 'Email is required',
        emailInvalid: 'Please enter a valid email',
        passwordRequired: 'Password is required',
        passwordLength: 'Password must be at least 8 characters',
        passwordUppercase: 'Password must contain at least one uppercase letter',
        passwordLowercase: 'Password must contain at least one lowercase letter',
        passwordNumber: 'Password must contain at least one number',
        nameRequired: 'Name is required',
        inviteRequired: 'Invite code is required',
        confirmRequired: 'Please confirm your password',
        confirmMatch: 'Passwords do not match',
      },
      resendVerification: {
        prompt: 'Your email address is not verified yet. We can send you a new verification link.',
        email: 'Email address',
        send: 'Send a new link',
        sending: 'Sending…',
        sent: 'If that address has an account that still needs verifying, we have sent a new link. It can take a few minutes to arrive. Check your spam folder too. If you asked recently, wait two minutes before asking again.',
        invalidEmail: 'Please enter a valid email address.',
        failed: 'We could not send that request. Please try again.',
      },
    },
    test: {
      pageTitle: 'Future Capability Test',
      eyebrow: 'Assessment',
      heroTitle: 'How future-capable are you?',
      heroSub: 'A structured assessment across the core dimensions of capability.',
      startBtn: 'Start the Test',
      loginToStart: 'Log In to Start',
      registerToStart: 'Create Account to Start',
      duration: `~${TEST_DURATION_MINUTES} minutes`,
      questions: 'Scenario-based',
      results: 'Instant results',
      retakes: 'Retakes available',
      dimensionsTitle: 'Six dimensions, mapped',
      structural: 'Structural Thinking',
      structuralDesc: 'Your ability to understand systems, identify patterns, and break down complex problems.',
      future: 'Future Judgment',
      futureDesc: 'Your ability to reason about uncertainty, spot trends, and plan across multiple time horizons.',
      complexity: 'Complexity Management',
      complexityDesc: 'Your ability to navigate ambiguity, manage trade-offs, and adapt to non-linear situations.',
      creativity: 'Creative Thinking',
      creativityDesc: 'Your ability to generate novel ideas, challenge assumptions, and find unexpected connections.',
      learning: 'Learning Agility',
      learningDesc: 'Your ability to acquire new knowledge, update mental models, and adapt your approach.',
      howTitle: 'How the test works',
      howStep1: 'Answer each scenario',
      howStep1Desc: 'Multiple-choice scenarios drawn from the full bank.',
      howStep2: 'Get your scores',
      howStep2Desc: 'Each answer shapes your starting read across every dimension.',
      howStep3: 'Track your progress',
      howStep3Desc: 'Come back and retake it to see how your starting read shifts over time.',
      faqTitle: 'Frequently asked questions',
      faq1Q: 'How long does the test take?',
      faq1A: 'Most people complete it in 15–20 minutes. There is no time limit.',
      faq2Q: 'Can I retake the test?',
      faq2A: 'Yes. You can retake the test as often as you like. Your scores are tracked over time.',
      faq3Q: 'What do the scores mean?',
      faq3A: 'Each dimension gets a 0–100 starting point — a first read, not a verdict — that moves as you take on challenges. Your total contributes to your Capability Credit (CC) score and tier.',
      faq4Q: 'Are the questions the same each time?',
      faq4A: 'Questions are selected from the full bank each time. Some may repeat.',
      sessionTitle: 'Future Capability Test',
      questionOf: 'Question {current} of {total}',
      answeredCount: '{count} of {total} answered',
      submitTest: 'Submit Test',
      submitting: 'Submitting…',
      confirmSubmit: 'Submit test?',
      confirmSubmitDesc: 'You have answered {answered} of {total} questions. Unanswered questions will be scored as incorrect. Are you sure?',
      selectAnswer: 'Select an answer to continue',
      resultTitle: 'Your Results',
      totalScore: 'Total Score',
      yourTier: 'Your Tier',
      breakdown: 'Score Breakdown',
      retakeTest: 'Retake Test',
      backToDashboard: 'Back to Dashboard',
      history: 'Test History',
      noHistory: 'You have not taken the test yet.',
      starting: 'Starting…',
      youllReceive: "You'll receive",
      // DB-01 (2026-09-18): preview1 was 'Your Future Capability Score' and preview2 'Your
      // capability type and profile' — the composite and its banded identity, named as the
      // assessment's deliverables. Rendered by TestStartPage, which is unrouted; removed
      // rather than left, on ALLOWED_UNROUTED's own standard.
      preview1: 'Your profile across five dimensions',
      preview3: 'Community ranking, once enough members join',
      preview4: 'Dimension-by-dimension breakdown',
    },
    newWord: {
      prizeEyebrow: 'New Word Prize',
      prizeTitle: 'Coin the words the future needs',
      prizeSub: 'Language shapes how we think. When the world changes faster than our words, new vocabulary becomes a strategic tool.',
      browseWords: 'Browse Words',
      submitWord: 'Submit a Word',
      directionsTitle: 'Three directions',
      technology: 'Technology',
      technologyDesc: 'Words describing new human-AI relationships, digital cognition, and the emergent behaviors of connected systems.',
      organization: 'Organization',
      organizationDesc: 'Words for new work structures, distributed leadership, adaptive teams, and post-hierarchical coordination.',
      lifestyle: 'Lifestyle',
      lifestyleDesc: 'Words capturing new social norms, identity frameworks, and human meaning-making in an accelerating world.',
      howTitle: 'How it works',
      howStep1: 'Submit your word',
      howStep1Desc: 'Provide the word, its definition, and the direction it belongs to.',
      howStep2: 'Community review',
      howStep2Desc: 'Members vote and comment. Top words move up the ranking pipeline.',
      howStep3: 'Win recognition',
      howStep3Desc: 'The best words enter the platform vocabulary and earn Future Capability Points.',
      examplesTitle: 'Example words',
      listEyebrow: 'Community Vocabulary',
      listTitle: 'New Words',
      listSub: 'Explore emerging concepts and vocabulary coined by our community.',
      submitCta: '+ Submit a Word',
      noWords: 'No words found',
      noWordsDesc: 'Try broadening your filters or be the first to contribute.',
      submitTitle: 'Submit a New Word',
      submitEyebrow: 'New Word Prize',
      submitSub: 'Propose a word that captures an emerging concept.',
      wordName: 'Word',
      wordNamePlaceholder: 'The new word or phrase',
      wordNameHint: 'Keep it concise — ideally 1–3 words.',
      definition: 'Definition',
      definitionPlaceholder: 'Explain clearly what this word means',
      definitionHint: 'Write a clear, precise definition. Be specific.',
      directionType: 'Direction',
      directionHint: 'Which category best fits this word?',
      exampleUsage: 'Example Usage',
      exampleUsagePlaceholder: 'Show the word used in a sentence',
      exampleHint: 'A concrete example helps readers understand how to use the word.',
      submitBtn: 'Submit Word',
      introTitle: 'New words can emerge in three dimensions of human experience',
      introPara1: "A great new word captures something real and important that the world needs language for. It might be a behaviour that's emerging, a technology becoming mainstream, a new organisational form, or a lifestyle shift on the horizon.",
      introPara2: "The best submissions name something the world is already experiencing but hasn't yet articulated.",
      submitDirWord: 'Submit {dir} Word →',
      examplesSubtitle: 'Words That Captured Emerging Concepts',
      ctaTitle: 'Help build the vocabulary of tomorrow',
      ctaDesc: 'Start by exploring existing words, or add your own to our collective vocabulary.',
      example1Word: 'Prompt Engineer',
      example1Def: 'A professional who specialises in crafting detailed instructions for AI systems to produce desired outputs.',
      example2Word: 'Synthetic Community',
      example2Def: 'Online communities where some members are AI and some are human, operating with shared norms and values.',
      example3Word: 'Quiet Ambition',
      example3Def: 'A personal value system prioritising meaningful impact and personal fulfilment over public recognition.',
    },
    thoughtChallenge: {
      eyebrow: 'Thought Challenge',
      heroTitle: 'There is no single right answer. That is the point.',
      heroSub: 'Open-ended questions that probe how you think — not just what you know.',
      browseChallenges: 'Browse Challenges',
      learnMore: 'Learn More',
      whatTitle: 'What are Thought Challenges?',
      whatDesc: 'Thought Challenges are carefully designed questions that have no single correct answer. They are designed to surface the quality of your reasoning, your ability to hold multiple perspectives, and your comfort with complexity.',
      highlightGlobal: 'Global Perspective',
      highlightGlobalDesc: 'Challenges draw on trends and tensions from across the world — not just one region or industry.',
      highlightCommunity: 'Community Wisdom',
      highlightCommunityDesc: 'The best answers are surfaced by community voting and editorial curation — not an algorithm.',
      highlightEarn: 'Earn Recognition',
      highlightEarnDesc: 'Featured answers earn Future Capability Points and visibility across the platform.',
      typesTitle: 'Three types of challenges',
      type1: 'Analytical',
      type1Desc: 'Break down a complex situation and identify the key forces at work.',
      type2: 'Predictive',
      type2Desc: 'Reason about possible futures and the conditions that would make each more likely.',
      type3: 'Normative',
      type3Desc: 'Argue for a position, acknowledging trade-offs and counterarguments.',
      stagesTitle: 'Stages of engagement',
      stage1: 'Read & Reflect',
      stage1Desc: 'Study the challenge prompt and gather your thoughts before responding.',
      stage2: 'Write & Submit',
      stage2Desc: 'Craft a structured, substantive answer. Depth and reasoning matter more than length.',
      stage3: 'Review & Vote',
      stage3Desc: 'Read other answers, vote for the ones you find most insightful, and engage in discussion.',
      listEyebrow: 'Community Challenges',
      listTitle: 'Thought Challenges',
      listSub: 'Pick a challenge, share your perspective, and engage with ideas from thinkers around the world.',
      noChallengess: 'No challenges found',
      noChallengesDesc: 'Check back soon — new challenges are added regularly.',
      opensCta: 'Opens {date}',
      closesCta: 'Closes {date}',
      submitAnswer: 'Submit Answer',
      submitAnswerEyebrow: 'Thought Challenge',
      yourAnswer: 'Your Answer',
      answerPlaceholder: 'Write your response here. Aim for depth and structure over length.',
      answerHint: 'Quality matters more than quantity. A clear, well-reasoned response of 200–400 words is often better than a lengthy one.',
      submitAnswerBtn: 'Submit Answer',
      answers: 'Answers',
      noAnswers: 'No answers yet. Be the first.',
      featured: 'Featured',
      likesCount: '{count} likes',
      whatGainTitle: 'What you gain',
      whatGoalNote: 'The goal is not to win. The goal is: to see the problem itself clearly.',
      ctaTitle: 'Enter the arena. Contribute your clearest thinking.',
      ctaDesc: 'Each challenge runs for a set period and invites community members to submit thoughtful answers. The best responses are highlighted, and all submissions contribute to our collective understanding of future possibilities.',
    },
    learningTheory: {
      eyebrow: 'Learning Theory Prize',
      heroTitle: 'Old theories fail.',
      heroTitleAccent: 'When the world accelerates, new ones must be created faster.',
      heroSub: 'An open platform for researchers, educators, and thinkers to propose and share theories about human learning, development, and adaptation in the age of AI.',
      submitTheory: 'Submit a Theory',
      browseTheories: 'Browse Theories',
      joinToSubmit: 'Join to Submit',
      aboutTitle: 'What the Learning Theory Prize is about',
      aboutDesc: 'The Learning Theory Prize is not an academic exercise. It is an open call to anyone who believes they have found a pattern — a principle that explains how humans can learn better, faster, or more deeply in a world being reshaped by artificial intelligence.',
      aboutHighlight: 'Submit your theory, get feedback from the community, and contribute to our collective understanding of how humans can better learn and grow.',
      whyTitle: 'What you gain',
      whyEyebrow: 'Why Participate',
      knowledgeTitle: 'Knowledge Sharing',
      knowledgeDesc: 'Share your insights and learn from thinkers across education, research, entrepreneurship, and beyond.',
      peerTitle: 'Peer Review',
      peerDesc: 'Get constructive feedback from the community — test your theory against real-world perspectives.',
      visibilityTitle: 'Visibility',
      visibilityDesc: 'Top theories receive spotlight across the platform. Exceptional theories become part of the core curriculum.',
      howTitle: 'How to Submit',
      howEyebrow: 'The Process',
      howStep1: 'Write Your Theory',
      howStep1Desc: 'Frame a clear, testable idea about how humans learn, adapt, or evolve — especially in the age of AI. The best theories are specific, provocative, and grounded.',
      howStep2: 'Submit for Review',
      howStep2Desc: 'Your theory enters the community feed where members can read, comment, like, and engage with your ideas.',
      howStep3: 'Rise Through Tiers',
      howStep3Desc: 'Highly rated theories gain visibility. Outstanding contributions become part of the platform\'s featured learning theory library.',
      featuredTitle: 'Featured Theories',
      featuredEyebrow: 'From the Community',
      listEyebrow: 'Community Contributions',
      listTitle: 'Learning Theories',
      listSub: 'Browse theories proposed by thinkers, researchers, and practitioners exploring how humans learn in the age of AI.',
      submitCta: '+ Submit a Theory',
      noTheories: 'No theories found',
      noTheoriesDesc: 'Try adjusting your filters, or be the first to contribute.',
      submitEyebrow: 'Learning Theory Prize',
      submitTitle: 'Submit a Theory',
      submitSub: 'Share your insights about how humans learn, adapt, and grow in the age of AI.',
      loginToSubmit: 'You need an account to share your theory with the community.',
      theoryTitle: 'Theory Title',
      theoryTitlePlaceholder: 'Give your theory a clear, compelling title',
      theoryTitleHint: 'A concise title that captures the essence of your theory.',
      summary: 'Summary',
      summaryPlaceholder: 'Summarize your theory in 2–3 sentences',
      summaryHint: 'A brief overview that helps readers understand your theory at a glance.',
      fullTheory: 'Full Theory',
      fullTheoryPlaceholder: 'Present your complete theory in detail…',
      fullTheoryHint: 'Provide a comprehensive explanation — include key concepts, mechanisms, and supporting reasoning.',
      applicationScenario: 'Application Scenario',
      applicationPlaceholder: 'How could this theory be applied in practice?',
      applicationHint: 'Describe a concrete scenario where this theory could be applied and what outcomes might result.',
      submitTheoryBtn: 'Submit Theory →',
      draftRestored: 'Draft restored from your last session.',
      discardDraft: 'Discard',
      completion: 'Completion',
      fieldsComplete: '{filled} of {total} fields complete',
      progressTitle: 'Your Progress',
      tipsTitle: 'Tips for a strong theory',
      tip1: 'Be specific — narrow theories are more testable and credible.',
      tip2: 'Ground your theory in observation or experience, not just intuition.',
      tip3: 'Explain the mechanism — why does this pattern hold?',
      tip4: 'Show real-world implications in your application scenario.',
      tip5: 'Your draft is saved automatically as you type.',
      viewAll: 'View All Theories →',
      ctaTitle: 'Share a theory the world needs to hear.',
      ctaDesc: 'If you have a clear, testable idea about how humans learn and grow, this platform is built for you.',
      example1Title: 'Abundance Era Creativity Learning Theory',
      example1Desc: 'AI makes creation approach infinity. Humans must imagine new structures rather than execute known ones.',
      example2Title: 'Uniquely Human Capability Theory',
      example2Desc: 'AI surpasses execution. The human core shifts to alignable mental structures that machines cannot replicate.',
      example3Title: 'Structural Recognition Learning Theory',
      example3Desc: 'The source of opportunity is structural change — not information, not effort, but the ability to see the shape of what is shifting.',
    },
    dashboard: {
      eyebrow: 'Your Account',
      heroTitle: 'Welcome back, {name}',
      tier: 'Tier',
      score: 'CC Score',
      fcp: 'FCP',
      member: 'Member',
      progressTo: 'Progress to {next}',
      atMaxTier: 'Maximum tier achieved',
      statsTitle: 'Activity',
      tests: 'Tests',
      theories: 'Theories',
      challenges: 'Challenges',
      words: 'Words',
      // KAN-478 / DB-01. `capabilityScore` / `capabilityScoreDesc` were removed, not renamed:
      // per the KAN-463 ruling the LABEL is the stronger of the two claims, so "Capability
      // Score" is the part that must not survive. Copy below is Paul's, settled 2026-09-17.
      // It is navigational — it says where the learner's capability lives, which is a fact
      // about the product rather than a measurement of the person.
      capabilityMapEyebrow: 'Your capability map',
      capabilityMapDesc: 'Your map shows where you stand, capability by capability.',
      capabilityMapCta: 'Open your map',
      quickLinks: 'Quick Actions',
      takeTest: 'Take the Test',
      takeTestSub: 'Get a read on your capability',
      browseTheories: 'Browse Theories',
      browseTheoriesSub: 'Learning theory library',
      submitWord: 'Submit a Word',
      submitWordSub: 'New word prize',
      viewChallenges: 'View Challenges',
      viewChallengesSub: 'Thought challenge questions',
      myReputation: 'My Reputation',
      myReputationSub: 'FCP score and tier',
      myInvites: 'My Invites',
      myInvitesSub: 'Invite codes',
      leaderboard: 'Leaderboard',
      leaderboardSub: 'Community rankings',
      myProfile: 'My Profile',
      myProfileSub: 'Edit your details',
      changePasswordTitle: 'Change Password',
      changePasswordToggle: 'Change Password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm new password',
      passwordStrength: 'Strength',
      strengthWeak: 'Weak',
      strengthFair: 'Fair',
      strengthGood: 'Good',
      strengthStrong: 'Strong',
      savePassword: 'Save Password',
      savingPassword: 'Saving…',
      passwordSuccess: 'Password changed successfully.',
      passwordCurrent: 'Current password',
      passwordNew: 'New password (min 8 chars, upper + lower + digit)',
      errors: {
        currentRequired: 'Current password is required',
        newRequired: 'New password is required',
        newLength: 'Must be at least 8 characters',
        newUppercase: 'Must include an uppercase letter',
        newLowercase: 'Must include a lowercase letter',
        newNumber: 'Must include a number',
        confirmRequired: 'Please confirm your new password',
        confirmMatch: 'Passwords do not match',
        samePassword: 'New password must differ from the current one',
      },
    },
    reputation: {
      eyebrow: 'Reputation',
      heroTitle: 'Your Reputation',
      tier: 'Tier',
      fcp: 'Future Capability Points',
      ccScore: 'CC Score',
      progressTo: 'Progress to {next}',
      atMax: 'Maximum tier achieved',
      journeyTitle: 'Tier Journey',
      current: 'Current',
      historyTitle: 'Reputation History',
      noHistory: 'No reputation history yet. Start contributing to earn FCP.',
      howTitle: 'How to Earn FCP',
      howEyebrow: 'Earn Points',
      earnTest: 'Complete the Future Capability Test',
      earnTestPoints: '50 FCP',
      earnWord: 'Submit a New Word',
      earnWordPoints: '100 FCP',
      earnTheory: 'Submit a Learning Theory',
      earnTheoryPoints: '120 FCP',
      earnChallenge: 'Answer a Thought Challenge',
      earnChallengePoints: '60 FCP',
      earnVote: 'Cast a Valid Vote',
      earnVotePoints: '3 FCP',
      earnInvite: 'Invite a Member',
      earnInvitePoints: 'Up to 100 FCP',
      recentActivity: 'Recent Activity',
    },
    invites: {
      eyebrow: 'Invite Program',
      heroTitle: 'Invite someone worth knowing',
      heroSub: 'The platform grows through trusted invitation. Each invite you send reflects your judgment about who belongs here.',
      available: 'Available',
      accepted: 'Accepted',
      totalQuota: 'Total Quota',
      quotaUsed: '{used} of {total} used this month',
      generateCode: 'Generate Invite Code',
      generating: 'Generating…',
      quotaExhausted: 'Monthly quota reached',
      codesTitle: 'Your Invite Codes',
      noCodes: 'You have not generated any invite codes yet.',
      copyCode: 'Copy',
      codeCopied: 'Copied!',
      howTitle: 'How It Works',
      howStep1: 'Generate a code',
      howStep1Desc: 'Each code is unique and single-use.',
      howStep2: 'Share it',
      howStep2Desc: 'Send it to someone you believe will contribute meaningfully.',
      howStep3: 'Earn together',
      howStep3Desc: 'When they join and contribute, you both earn FCP.',
      statusPending: 'Pending',
      statusAccepted: 'Accepted',
      statusExpired: 'Expired',
    },
    leaderboard: {
      title: 'Leaderboard',
      eyebrow: 'Community Rankings',
      contributions: 'Contributions',
      newWords: 'New Words',
      invites: 'Invites',
      rank: 'Rank',
      member: 'Member',
      score: 'Score',
      tier: 'Tier',
      noData: 'No data available yet.',
      yourRank: 'Your Rank',
    },
    welcome: {
      title: 'Welcome to Future Capability',
      sub: 'Your account has been created. Here is what you can do next.',
      step1: 'Take the test',
      step1Desc: 'Establish your baseline capability score.',
      step2: 'Explore challenges',
      step2Desc: 'Pick a thought challenge and share your perspective.',
      step3: 'Submit a word',
      step3Desc: 'Coin a new word that captures an emerging concept.',
      cta: 'Go to Dashboard',
    },
    footer: {
      tagline: 'Building human capability for an uncertain future.',
      links: 'Platform',
      test: 'Capability Test',
      newWords: 'New Words',
      challenges: 'Challenges',
      theories: 'Theories',
      leaderboard: 'Leaderboard',
      rights: 'All rights reserved.',
    },
    errors: {
      notFound: 'Page not found',
      notFoundDesc: 'The page you are looking for does not exist.',
      backHome: 'Back to Home',
      unauthorized: 'You need to be logged in to view this page.',
      forbidden: 'You do not have permission to view this page.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CHINESE (SIMPLIFIED / 简体中文)
  // ─────────────────────────────────────────────────────────────────────────
  zh: {
    common: {
      loading: '加载中…',
      retry: '重试',
      save: '保存',
      cancel: '取消',
      submit: '提交',
      back: '返回',
      next: '下一步',
      previous: '上一步',
      close: '关闭',
      confirm: '确认',
      delete: '删除',
      edit: '编辑',
      view: '查看 →',
      copy: '复制',
      copied: '已复制！',
      search: '搜索',
      filter: '筛选',
      sortBy: '排序方式',
      status: '状态',
      type: '类型',
      all: '全部',
      none: '无',
      yes: '是',
      no: '否',
      or: '或',
      and: '和',
      by: '作者',
      of: '/',
      page: '第',
      error: '错误',
      success: '成功',
      required: '必填',
      optional: '选填',
      home: '首页',
      dashboard: '控制台',
      profile: '个人资料',
      logout: '退出登录',
      login: '登录',
      register: '注册',
      notFound: '未找到',
      somethingWrong: '出现错误，请重试。',
      noResults: '未找到相关结果。',
      allTypes: '所有类型',
      word: '个词汇',
      words: '个词汇',
      challenge: '个挑战',
      challenges: '个挑战',
      theory: '个理论',
      theories: '个理论',
      sortLatest: '最新',
      sortPopular: '最热门',
      sortTop: '评分最高',
      sortOldest: '最早',
    },
    status: {
      active: '进行中',
      upcoming: '即将开始',
      closed: '已关闭',
      archived: '已归档',
      published: '已发布',
      pending: '待审核',
      rejected: '已拒绝',
      submitted: '已提交',
      under_review: '审核中',
      draft: '草稿',
      winner: '获奖',
      top20: '前20名',
      top100: '前100名',
      reviewing: '评审中',
    },
    nav: {
      newWordPrize: '新词奖',
      test: '能力测评',
      thoughtChallenge: '思维挑战',
      learningTheory: '学习理论',
      profile: '个人资料',
      myInvites: '我的邀请',
      reputation: '声誉',
      logout: '退出登录',
      login: '登录',
      register: '注册',
      openMenu: '打开导航菜单',
      closeMenu: '关闭导航菜单',
    },
    home: {
      heroEyebrow: '未来能力平台',
      heroTitle: '衡量并发展你面向未来的能力',
      heroSub: '一个结构化学习、协作挑战、词汇创造与能力评分的社区平台。',
      heroCta: '立即开始',
      heroSecondary: '了解更多',
      featuresTitle: '四种能力提升途径',
      featuresSub: '每个模块培养面向未来思维的不同维度。',
      testTitle: '未来能力测评',
      testDesc: '跨越五个维度的结构化评估：结构性思维、未来判断、复杂性管理、创造性思维与学习敏捷性。',
      testCta: '开始测评',
      wordTitle: '新词奖',
      wordDesc: '为技术、组织和生活方式领域的新兴概念创造词汇。',
      wordCta: '探索词汇',
      challengeTitle: '思维挑战',
      challengeDesc: '参与没有唯一答案的开放性问题，凭借卓越思维获得认可。',
      challengeCta: '查看挑战',
      theoryTitle: '学习理论奖',
      theoryDesc: '提出并分享关于人类在人工智能时代如何学习、适应和成长的理论。',
      theoryCta: '浏览理论',
      howTitle: '如何使用',
      howStep1: '创建账户',
      howStep1Desc: '使用现有成员的邀请码加入。',
      howStep2: '完成测评',
      howStep2Desc: '参加未来能力测评，确立你的基准分数。',
      howStep3: '贡献与获益',
      howStep3Desc: '提交词汇、理论和挑战答案，赚取未来能力积分。',
      howStep4: '晋级提升',
      howStep4Desc: '随着分数增长，从探索者晋升至先驱者。',
      ctaTitle: '准备好衡量你的未来能力了吗？',
      ctaSub: '加入一个共同构建未来所需技能的思想者社区。',
      ctaBtn: '创建账户',
      leaderboardLink: '查看排行榜',
    },
    auth: {
      loginTitle: '欢迎回来',
      loginSub: '登录你的账户',
      email: '电子邮箱',
      emailPlaceholder: 'you@example.com',
      password: '密码',
      passwordPlaceholder: '你的密码',
      showPassword: '显示',
      hidePassword: '隐藏',
      forgotPassword: '忘记密码？',
      loginBtn: '登录',
      loggingIn: '登录中…',
      noAccount: '还没有账户？',
      registerLink: '立即创建',
      registerTitle: '创建账户',
      registerSub: '使用邀请码加入',
      name: '姓名',
      namePlaceholder: '你的姓名',
      inviteCode: '邀请码',
      inviteCodePlaceholder: '输入你的邀请码',
      inviteCodeHint: '可选 — 仅在你收到邀请码时填写。',
      confirmPassword: '确认密码',
      confirmPasswordPlaceholder: '再次输入密码',
      passwordHint: '至少8个字符，包含大写字母、小写字母和数字',
      registerBtn: '创建账户',
      registering: '创建中…',
      haveAccount: '已有账户？',
      loginLink: '立即登录',
      errors: {
        emailRequired: '请输入电子邮箱',
        emailInvalid: '请输入有效的电子邮箱',
        passwordRequired: '请输入密码',
        passwordLength: '密码至少需要8个字符',
        passwordUppercase: '密码必须包含至少一个大写字母',
        passwordLowercase: '密码必须包含至少一个小写字母',
        passwordNumber: '密码必须包含至少一个数字',
        nameRequired: '请输入姓名',
        inviteRequired: '请输入邀请码',
        confirmRequired: '请确认密码',
        confirmMatch: '两次输入的密码不一致',
      },
      resendVerification: {
        prompt: '你的邮箱地址尚未验证。我们可以给你发送一个新的验证链接。',
        email: '电子邮箱',
        send: '发送新链接',
        sending: '发送中…',
        sent: '如果该地址对应的账户仍需验证，我们已发送新的链接。邮件可能需要几分钟才能到达，也请查看垃圾邮件文件夹。如果你刚刚申请过，请等待两分钟后再试。',
        invalidEmail: '请输入有效的邮箱地址。',
        failed: '无法发送该请求，请重试。',
      },
    },
    test: {
      pageTitle: '未来能力测评',
      eyebrow: '能力评估',
      heroTitle: '你的未来能力如何？',
      heroSub: '一套涵盖核心能力维度的结构化评估。',
      startBtn: '开始测评',
      loginToStart: '登录后开始',
      registerToStart: '注册后开始',
      duration: `约${TEST_DURATION_MINUTES}分钟`,
      questions: '20道题目',
      results: '即时结果',
      retakes: '可重复测评',
      dimensionsTitle: '六个评估维度',
      structural: '结构性思维',
      structuralDesc: '理解系统、识别规律、分解复杂问题的能力。',
      future: '未来判断力',
      futureDesc: '推理不确定性、识别趋势、跨时间维度规划的能力。',
      complexity: '复杂性管理',
      complexityDesc: '应对模糊性、管理权衡取舍、适应非线性情况的能力。',
      creativity: '创造性思维',
      creativityDesc: '产生新想法、挑战假设、发现意想不到联系的能力。',
      learning: '学习敏捷性',
      learningDesc: '获取新知识、更新思维模型、调整策略的能力。',
      howTitle: '测评如何进行',
      howStep1: '回答20道题',
      howStep1Desc: '从完整题库中随机抽取20道多选题。',
      howStep2: '获取分数',
      howStep2Desc: '每道答案立即在各个维度上计分。',
      howStep3: '追踪进步',
      howStep3Desc: '重复参加测评，衡量你随时间的进步。',
      faqTitle: '常见问题',
      faq1Q: '测评需要多长时间？',
      faq1A: '大多数人在15–20分钟内完成。没有时间限制。',
      faq2Q: '可以重测吗？',
      faq2A: '可以。你可以随时重测。你的分数会随时间追踪记录。',
      faq3Q: '分数代表什么？',
      faq3A: '每个维度的满分为100分。总分将计入你的能力信用（CC）分数和等级。',
      faq4Q: '每次的题目相同吗？',
      faq4A: '每次从完整题库中抽取20道题，部分题目可能重复出现。',
      sessionTitle: '未来能力测评',
      questionOf: '第 {current} 题，共 {total} 题',
      answeredCount: '已回答 {count} / {total} 题',
      submitTest: '提交测评',
      submitting: '提交中…',
      confirmSubmit: '确认提交？',
      confirmSubmitDesc: '你已回答 {answered} / {total} 题。未回答的题目将计为错误。确定提交吗？',
      selectAnswer: '请选择一个答案',
      resultTitle: '你的测评结果',
      totalScore: '总分',
      yourTier: '你的等级',
      breakdown: '各维度得分',
      retakeTest: '重新测评',
      backToDashboard: '返回控制台',
      history: '测评历史',
      noHistory: '你尚未参加测评。',
      starting: '启动中…',
      youllReceive: '你将获得',
      // DB-01 (2026-09-18): the zh half of the same removal.
      preview1: '五个维度的能力画像',
      preview3: '全球及城市排名',
      preview4: '各维度详细分析',
    },
    newWord: {
      prizeEyebrow: '新词奖',
      prizeTitle: '为未来创造词汇',
      prizeSub: '语言塑造思维方式。当世界变化的速度超过我们的词汇，新词汇就成为战略工具。',
      browseWords: '浏览词汇',
      submitWord: '提交词汇',
      directionsTitle: '三个方向',
      technology: '科技',
      technologyDesc: '描述人机关系、数字认知及互联系统新兴行为的词汇。',
      organization: '组织',
      organizationDesc: '描述新型工作结构、分布式领导、自适应团队和后层级协调的词汇。',
      lifestyle: '生活方式',
      lifestyleDesc: '捕捉加速变化世界中新社会规范、身份框架和人类意义构建的词汇。',
      howTitle: '如何参与',
      howStep1: '提交词汇',
      howStep1Desc: '提供词汇、定义及所属方向。',
      howStep2: '社区评审',
      howStep2Desc: '成员投票和评论。最佳词汇晋升排名。',
      howStep3: '获得认可',
      howStep3Desc: '最优词汇进入平台词汇库并赚取未来能力积分。',
      examplesTitle: '示例词汇',
      listEyebrow: '社区词汇',
      listTitle: '新词汇',
      listSub: '探索社区创造的新兴概念与词汇。',
      submitCta: '+ 提交词汇',
      noWords: '未找到词汇',
      noWordsDesc: '尝试扩大筛选范围，或成为第一个贡献者。',
      submitTitle: '提交新词汇',
      submitEyebrow: '新词奖',
      submitSub: '提出一个描述新兴概念的词汇。',
      wordName: '词汇',
      wordNamePlaceholder: '新词汇或短语',
      wordNameHint: '保持简洁——理想情况下1–3个词。',
      definition: '定义',
      definitionPlaceholder: '清晰解释这个词的含义',
      definitionHint: '写一个清晰、精确的定义，越具体越好。',
      directionType: '方向',
      directionHint: '哪个类别最符合这个词汇？',
      exampleUsage: '使用示例',
      exampleUsagePlaceholder: '展示这个词在句子中的使用方法',
      exampleHint: '一个具体示例有助于读者理解如何使用这个词。',
      submitBtn: '提交词汇',
      introTitle: '新词可以从人类体验的三个维度中涌现',
      introPara1: '一个好的新词能捕捉世界需要语言表达的真实而重要的事物。它可能是一种正在出现的行为、一项逐渐主流化的技术、一种新的组织形式，或是一个即将到来的生活方式转变。',
      introPara2: '最佳投稿能够命名世界已在经历、但尚未清晰表达的事物。',
      submitDirWord: '提交{dir}词汇 →',
      examplesSubtitle: '捕捉新兴概念的词汇',
      ctaTitle: '帮助构建明日词汇',
      ctaDesc: '先浏览现有词汇，或将你的词汇加入我们的集体词汇库。',
      example1Word: 'Prompt Engineer',
      example1Def: '一种专门为AI系统制作详细指令以产生所需输出的职业。',
      example2Word: 'Synthetic Community',
      example2Def: '部分成员是AI、部分是人类、在共同规范和价值观下运作的在线社区。',
      example3Word: 'Quiet Ambition',
      example3Def: '一种优先考虑有意义的影响和个人成就感而非公众认可的个人价值体系。',
    },
    thoughtChallenge: {
      eyebrow: '思维挑战',
      heroTitle: '没有唯一正确答案。这正是关键所在。',
      heroSub: '开放性问题，探究你的思维方式——而不仅仅是你所知道的内容。',
      browseChallenges: '浏览挑战',
      learnMore: '了解更多',
      whatTitle: '什么是思维挑战？',
      whatDesc: '思维挑战是精心设计的、没有唯一正确答案的问题。它们旨在呈现你的推理质量、持有多种视角的能力，以及你对复杂性的适应程度。',
      highlightGlobal: '全球视角',
      highlightGlobalDesc: '挑战题目涵盖来自世界各地的趋势与张力——不限于某一地区或行业。',
      highlightCommunity: '社区智慧',
      highlightCommunityDesc: '最佳答案通过社区投票和编辑评选产生——而非算法。',
      highlightEarn: '获得认可',
      highlightEarnDesc: '精选答案可赚取未来能力积分，并在平台上获得展示。',
      typesTitle: '三种挑战类型',
      type1: '分析型',
      type1Desc: '分解复杂情况，识别关键作用力。',
      type2: '预测型',
      type2Desc: '推理可能的未来，以及使每种未来更可能发生的条件。',
      type3: '规范型',
      type3Desc: '为某一立场辩护，同时承认权衡和反驳意见。',
      stagesTitle: '参与阶段',
      stage1: '阅读与思考',
      stage1Desc: '仔细研读挑战题目，在作答前整理思路。',
      stage2: '写作与提交',
      stage2Desc: '撰写结构清晰、内容充实的答案。深度和推理比篇幅更重要。',
      stage3: '评阅与投票',
      stage3Desc: '阅读其他答案，为你认为最有见地的答案投票，并参与讨论。',
      listEyebrow: '社区挑战',
      listTitle: '思维挑战',
      listSub: '选择一个挑战，分享你的观点，与来自世界各地的思考者交流。',
      noChallengess: '未找到挑战',
      noChallengesDesc: '请稍后再来——新挑战会定期添加。',
      opensCta: '开始于 {date}',
      closesCta: '截止于 {date}',
      submitAnswer: '提交答案',
      submitAnswerEyebrow: '思维挑战',
      yourAnswer: '你的答案',
      answerPlaceholder: '在此写下你的回答。注重深度和结构，而非篇幅。',
      answerHint: '质量比数量更重要。一个清晰、有条理的200–400字回答通常优于冗长的答案。',
      submitAnswerBtn: '提交答案',
      answers: '答案',
      noAnswers: '暂无答案，成为第一个回答的人。',
      featured: '精选',
      likesCount: '{count} 个赞',
      whatGainTitle: '你能获得什么',
      whatGoalNote: '目标不是赢。目标是：清楚地看到问题本身。',
      ctaTitle: '进入竞技场，贡献你最清晰的思考。',
      ctaDesc: '每个挑战在设定的时间段内运行，邀请社区成员提交深思熟虑的答案。最佳回答将被重点展示，所有提交内容都将为我们对未来可能性的集体理解做出贡献。',
    },
    learningTheory: {
      eyebrow: '学习理论奖',
      heroTitle: '旧理论已经失效。',
      heroTitleAccent: '当世界加速，新理论必须被更快地创造出来。',
      heroSub: '一个开放平台，供研究人员、教育者和思考者提出和分享关于人类学习、发展与适应（尤其是在人工智能时代）的理论。',
      submitTheory: '提交理论',
      browseTheories: '浏览理论',
      joinToSubmit: '注册后提交',
      aboutTitle: '关于学习理论奖',
      aboutDesc: '学习理论奖不是学术练习，而是一个开放邀约——向任何相信自己发现了某种规律的人发出，这种规律能解释人类在被人工智能重塑的世界中如何更好、更快或更深入地学习。',
      aboutHighlight: '提交你的理论，获取社区反馈，为我们共同理解人类如何更好地学习和成长做出贡献。',
      whyTitle: '你能获得什么',
      whyEyebrow: '为什么参与',
      knowledgeTitle: '知识共享',
      knowledgeDesc: '分享你的见解，向教育、研究、创业等各领域的思考者学习。',
      peerTitle: '同行评审',
      peerDesc: '获得社区的建设性反馈——用真实视角检验你的理论。',
      visibilityTitle: '获得曝光',
      visibilityDesc: '顶尖理论将在平台上获得重点展示，优秀理论将成为核心课程的一部分。',
      howTitle: '如何提交',
      howEyebrow: '参与流程',
      howStep1: '撰写理论',
      howStep1Desc: '阐述一个关于人类如何学习、适应或进化的清晰、可检验的观点——尤其是在人工智能时代。最好的理论是具体的、有挑战性的，且有据可查的。',
      howStep2: '提交评审',
      howStep2Desc: '你的理论进入社区内容流，成员可以阅读、评论、点赞和参与讨论。',
      howStep3: '晋级提升',
      howStep3Desc: '高评分理论获得更多曝光。杰出贡献将成为平台精选学习理论库的一部分。',
      featuredTitle: '精选理论',
      featuredEyebrow: '来自社区',
      listEyebrow: '社区贡献',
      listTitle: '学习理论',
      listSub: '浏览思考者、研究者和从业者提出的关于人类在人工智能时代如何学习的理论。',
      submitCta: '+ 提交理论',
      noTheories: '未找到理论',
      noTheoriesDesc: '尝试调整筛选条件，或成为第一个贡献者。',
      submitEyebrow: '学习理论奖',
      submitTitle: '提交理论',
      submitSub: '分享你关于人类如何在人工智能时代学习、适应和成长的见解。',
      loginToSubmit: '你需要一个账户才能与社区分享你的理论。',
      theoryTitle: '理论标题',
      theoryTitlePlaceholder: '为你的理论起一个清晰、引人注目的标题',
      theoryTitleHint: '一个简洁捕捉理论核心的标题。',
      summary: '摘要',
      summaryPlaceholder: '用2–3句话概括你的理论',
      summaryHint: '帮助读者快速了解你的理论的简短概述。',
      fullTheory: '完整理论',
      fullTheoryPlaceholder: '详细呈现你的完整理论…',
      fullTheoryHint: '提供全面的阐述——包括核心概念、机制和支持推理。',
      applicationScenario: '应用场景',
      applicationPlaceholder: '这个理论在实践中如何应用？',
      applicationHint: '描述一个可以应用此理论的具体场景以及可能产生的结果。',
      submitTheoryBtn: '提交理论 →',
      draftRestored: '已从上次会话恢复草稿。',
      discardDraft: '丢弃',
      completion: '完成度',
      fieldsComplete: '{filled} / {total} 个字段已完成',
      progressTitle: '你的进度',
      tipsTitle: '强有力理论的技巧',
      tip1: '具体化——范围更窄的理论更具可检验性和可信度。',
      tip2: '将理论建立在观察或经验上，而非仅靠直觉。',
      tip3: '解释机制——为什么这种规律成立？',
      tip4: '在应用场景中展示现实世界的影响。',
      tip5: '草稿会在你输入时自动保存。',
      viewAll: '查看所有理论 →',
      ctaTitle: '分享一个世界需要听到的理论。',
      ctaDesc: '如果你有关于人类如何学习和成长的清晰、可检验的想法，这个平台就是为你而建的。',
      example1Title: '丰盛时代创造力学习理论',
      example1Desc: 'AI使创造趋近无限。人类必须想象新结构，而不是执行已知的结构。',
      example2Title: '人类独特能力理论',
      example2Desc: 'AI超越了执行能力。人类核心转向机器无法复制的可对齐心智结构。',
      example3Title: '结构识别学习理论',
      example3Desc: '机遇的来源是结构性变化——不是信息，不是努力，而是看到正在转变之形态的能力。',
    },
    dashboard: {
      eyebrow: '你的账户',
      heroTitle: '欢迎回来，{name}',
      tier: '等级',
      score: 'CC 分数',
      fcp: '未来能力积分',
      member: '成员',
      progressTo: '距 {next} 的进度',
      atMaxTier: '已达到最高等级',
      statsTitle: '活动记录',
      tests: '测评',
      theories: '理论',
      challenges: '挑战',
      words: '词汇',
      // KAN-478 / DB-01. `能力分数` was the zh half of the breach and had to go with the English
      // one — a criterion does not stop at a locale boundary.
      //
      // NEEDS A NATIVE PASS BEFORE IT IS TREATED AS AUTHORITATIVE. Paul settled the English and
      // deliberately did not write this pair, on the grounds that a loose translation of a claim
      // we have just spent a week making precise is not worth the saving. That reasoning is
      // right about a claim. These three strings are navigational — they name a destination and
      // assert nothing about the learner — which is why they were drafted rather than left as
      // untranslated keys, since `t()` has no en fallback and a missing key renders its own path
      // on screen. Replace freely; nothing here is a ruling.
      capabilityMapEyebrow: '你的能力图谱',
      capabilityMapDesc: '你的图谱按能力逐项显示你所处的位置。',
      capabilityMapCta: '打开你的图谱',
      quickLinks: '快速操作',
      takeTest: '参加测评',
      takeTestSub: '衡量你的能力',
      browseTheories: '浏览理论',
      browseTheoriesSub: '学习理论库',
      submitWord: '提交词汇',
      submitWordSub: '新词奖',
      viewChallenges: '查看挑战',
      viewChallengesSub: '思维挑战题目',
      myReputation: '我的声誉',
      myReputationSub: '积分和等级',
      myInvites: '我的邀请',
      myInvitesSub: '邀请码',
      leaderboard: '排行榜',
      leaderboardSub: '社区排名',
      myProfile: '我的资料',
      myProfileSub: '编辑个人信息',
      changePasswordTitle: '修改密码',
      changePasswordToggle: '修改密码',
      currentPassword: '当前密码',
      newPassword: '新密码',
      confirmNewPassword: '确认新密码',
      passwordStrength: '强度',
      strengthWeak: '弱',
      strengthFair: '一般',
      strengthGood: '良好',
      strengthStrong: '强',
      savePassword: '保存密码',
      savingPassword: '保存中…',
      passwordSuccess: '密码修改成功。',
      passwordCurrent: '当前密码',
      passwordNew: '新密码（至少8个字符，需含大小写字母和数字）',
      errors: {
        currentRequired: '请输入当前密码',
        newRequired: '请输入新密码',
        newLength: '至少需要8个字符',
        newUppercase: '必须包含大写字母',
        newLowercase: '必须包含小写字母',
        newNumber: '必须包含数字',
        confirmRequired: '请确认新密码',
        confirmMatch: '两次输入的密码不一致',
        samePassword: '新密码必须与当前密码不同',
      },
    },
    reputation: {
      eyebrow: '声誉',
      heroTitle: '你的声誉',
      tier: '等级',
      fcp: '未来能力积分',
      ccScore: 'CC 分数',
      progressTo: '距 {next} 的进度',
      atMax: '已达到最高等级',
      journeyTitle: '等级旅程',
      current: '当前',
      historyTitle: '声誉历史',
      noHistory: '暂无声誉记录。开始贡献以赚取积分。',
      howTitle: '如何赚取积分',
      howEyebrow: '积分获取',
      earnTest: '完成未来能力测评',
      earnTestPoints: '50 积分',
      earnWord: '提交新词汇',
      earnWordPoints: '100 积分',
      earnTheory: '提交学习理论',
      earnTheoryPoints: '120 积分',
      earnChallenge: '回答思维挑战',
      earnChallengePoints: '60 积分',
      earnVote: '有效投票',
      earnVotePoints: '3 积分',
      earnInvite: '邀请成员',
      earnInvitePoints: '最高 100 积分',
      recentActivity: '近期活动',
    },
    invites: {
      eyebrow: '邀请计划',
      heroTitle: '邀请值得认识的人',
      heroSub: '平台通过可信邀请增长。你发送的每一个邀请都体现了你对谁应该加入的判断。',
      available: '可用',
      accepted: '已接受',
      totalQuota: '本月配额',
      quotaUsed: '本月已使用 {used} / {total}',
      generateCode: '生成邀请码',
      generating: '生成中…',
      quotaExhausted: '本月配额已用完',
      codesTitle: '你的邀请码',
      noCodes: '你尚未生成任何邀请码。',
      copyCode: '复制',
      codeCopied: '已复制！',
      howTitle: '如何使用',
      howStep1: '生成邀请码',
      howStep1Desc: '每个邀请码唯一且仅能使用一次。',
      howStep2: '分享邀请码',
      howStep2Desc: '将邀请码发送给你认为会做出有意义贡献的人。',
      howStep3: '共同获益',
      howStep3Desc: '当他们加入并贡献后，你们双方都能赚取积分。',
      statusPending: '待使用',
      statusAccepted: '已接受',
      statusExpired: '已过期',
    },
    leaderboard: {
      title: '排行榜',
      eyebrow: '社区排名',
      contributions: '贡献',
      newWords: '新词汇',
      invites: '邀请',
      rank: '排名',
      member: '成员',
      score: '分数',
      tier: '等级',
      noData: '暂无数据。',
      yourRank: '你的排名',
    },
    welcome: {
      title: '欢迎来到未来能力平台',
      sub: '你的账户已创建。以下是你接下来可以做的事情。',
      step1: '参加测评',
      step1Desc: '建立你的基准能力分数。',
      step2: '探索挑战',
      step2Desc: '选择一个思维挑战，分享你的观点。',
      step3: '提交词汇',
      step3Desc: '创造一个描述新兴概念的词汇。',
      cta: '前往控制台',
    },
    footer: {
      tagline: '为不确定的未来培养人类能力。',
      links: '平台',
      test: '能力测评',
      newWords: '新词汇',
      challenges: '思维挑战',
      theories: '学习理论',
      leaderboard: '排行榜',
      rights: '版权所有。',
    },
    errors: {
      notFound: '页面未找到',
      notFoundDesc: '你访问的页面不存在。',
      backHome: '返回首页',
      unauthorized: '你需要登录才能查看此页面。',
      forbidden: '你没有权限查看此页面。',
    },
  },
};

export default translations;
